import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { detectLocale, persistLocale, t as translate, type I18nKey, type I18nValues, type LocaleCode } from './core';

export type I18nContextValue = {
  locale: LocaleCode;
  setLocale: (locale: LocaleCode) => void;
  t: (key: I18nKey | string, values?: I18nValues) => string;
};

const fallbackContext: I18nContextValue = {
  locale: 'en-US',
  setLocale: () => undefined,
  t: (key, values) => translate(key, 'en-US', values),
};

const I18nContext = createContext<I18nContextValue>(fallbackContext);

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<LocaleCode>(() => detectLocale());

  const setLocale = useCallback((nextLocale: LocaleCode) => {
    setLocaleState(nextLocale);
    persistLocale(nextLocale);
  }, []);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const value = useMemo<I18nContextValue>(() => ({
    locale,
    setLocale,
    t: (key, values) => translate(key, locale, values),
  }), [locale, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}
